<template>
    <div class="mx-auto w-full rounded-[6px] bg-white lg:mx-0">
        <div
            class="mt-5 flex items-start justify-between gap-2 overflow-hidden px-6 pt-1"
        >
            <div class="">
                <div class="flex h-10 w-40 shrink">
                    <slot name="logo" />
                </div>

                <div class="mt-6">
                    <div class="mt-2 flex flex-wrap items-center gap-2">
                        <div
                            v-if="badge"
                            class="w-max rounded-[6px] bg-green-200 px-3 py-[3px] text-xs font-medium leading-normal text-green-800"
                        >
                            {{ badge }}
                        </div>

                        <div
                            class="w-max rounded-[6px] bg-blue-200 px-3 py-[3px] text-xs font-medium leading-normal text-gray-700"
                        >
                            {{ integrationType }} integration
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="active"
                class="flex flex-wrap-reverse items-center justify-end gap-3"
            >
                <AtomicButton
                    type="button"
                    color="white"
                    size="sm"
                    class="flex h-10 w-10 items-center justify-center"
                    style="padding: 0px !important"
                    @click="$emit('openRemove')"
                >
                    <TrashIcon class="h-5 w-5 text-gray-400" />
                </AtomicButton>
                <AtomicButton
                    type="button"
                    color="white"
                    size="sm"
                    class="flex h-10 items-center justify-center"
                    @click="$emit('openEdit')"
                >
                    Edit
                </AtomicButton>
            </div>
            <div v-else class="hidden sm:block">
                <LockableButton
                    :locked="requiresSubscription && !isProSubscribedOrTrialing"
                    :lock-message="`You need a ${planName} subscription to enable this integration.`"
                    :disabled="disabled"
                    type="button"
                    size="md"
                    color="yellow"
                    @click="$emit('openNew')"
                >
                    Enable
                </LockableButton>
            </div>
        </div>
        <div
            v-if="disabled"
            class="mx-6 mt-3 rounded-[4px] bg-yellow-100 px-[14px] py-3"
        >
            <div class="flex flex-col items-start sm:flex-row">
                <ExclamationTriangleIcon
                    class="mr-[14px] h-5 w-5 shrink-0 text-yellow-600"
                />
                <div class="mt-2 sm:mt-0">
                    <div class="text-sm font-medium text-yellow-800">
                        Integration stopped
                    </div>
                    <div class="mt-[7px] text-sm text-yellow-700">
                        This is why this integration is not active. You should
                        do something about it to get it running again.
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col p-6 text-base text-gray-500">
            <div v-if="active">
                <div class="rounded-[4px] border border-gray-200 bg-gray-50">
                    <div class="">
                        <div class="p-4">
                            <div class="flex flex-wrap gap-2">
                                <div class="text-gray-500">Name:</div>
                                <div class="text-gray-800">
                                    {{ name }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else>
                <div v-if="text" v-html="text"></div>
                <div v-else>
                    Create an email template in TOPOL and send it with
                    {{ title }}.
                </div>
            </div>
            <div v-if="documentationUrl" class="mt-6">
                <a
                    class="inline text-topol-blue-500 hover:text-topol-blue-600"
                    :href="documentationUrl"
                    target="_blank"
                    >Check out the documentation</a
                >
                on how to integrate with {{ title }}.
            </div>
        </div>
        <div v-if="!active" class="pb-6 pl-6 sm:hidden">
            <AtomicButton
                :disabled="disabled"
                type="button"
                color="yellow"
                @click="$emit('openNew')"
            >
                Enable integration
            </AtomicButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { TrashIcon } from '@heroicons/vue/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';
import { PropType } from 'vue';
import { useSubscriptionLabel } from '@/composables/useSubscriptionLabel';

defineProps({
    active: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        required: true,
    },
    text: {
        type: String,
        required: false,
        default: '',
    },
    documentationUrl: {
        type: String,
        required: false,
        default: '',
    },
    integrationType: {
        type: String as PropType<'Sign-in' | 'API'>,
        required: true,
    },
    name: {
        type: String,
        required: false,
        default: '',
    },
    /**
     * Most integrations are a paid feature. Lettr is not - sending people to
     * Lettr is the point - so it opts out of the subscription lock.
     */
    requiresSubscription: {
        type: Boolean,
        default: true,
    },
    /**
     * Optional pill shown next to the integration type, for flagging a freshly
     * added integration. Empty on every box that has been around a while.
     */
    badge: {
        type: String,
        required: false,
        default: '',
    },
});

const { isProSubscribedOrTrialing } = useSubscriptions();
const { planName } = useSubscriptionLabel();

defineEmits(['openNew', 'openEdit', 'openRemove']);
</script>
